import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Box,
  Button,
  CameraIcon,
  CloseIcon,
  Flex,
  IconButton,
  Text,
} from '@hivewatch/dark'

import { Spinner } from '@/components/ui'
import { VideoPlayer, useCameraFeed } from '@/features/video-player'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  removeCameraAtCell,
  selectVideoWallCameraCount,
} from '@/redux/videoWall/videoWallSlice'
import { trackEvent } from '@/utils/analytics'

import { VIDEO_WALL_ANALYTICS } from '../constants/constants'
import type { CellCameraIProps } from '../types/types'

interface IProps {
  camera: CellCameraIProps
  index: number
}

/**
 * A populated grid slot: the live camera feed with a hover overlay (camera name
 * + remove). The feed URL is resolved with the shared useCameraFeed hook and
 * rendered with the url-based VideoPlayer, which is contained to 16:9 inside the
 * cell (letterboxed). `suppressGlobalPlayState` is required so six concurrent
 * players don't thrash the Incident Drawer's global play state.
 */
export const VideoWallTile = ({ camera, index }: IProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const cameraCount = useAppSelector(selectVideoWallCameraCount)
  const { url, isLoading, errorMessage, refetch } = useCameraFeed(
    camera.deviceId
  )
  const [isHovered, setIsHovered] = useState(false)

  const handleRemove = () => {
    dispatch(removeCameraAtCell({ index }))
    trackEvent(VIDEO_WALL_ANALYTICS.cameraRemoved, {
      camera_id: camera.deviceId,
      cell_index: index,
      camera_count: Math.max(0, cameraCount - 1),
    })
  }

  return (
    <Box
      alignItems='center'
      bg='bg.card'
      borderRadius='md'
      data-testid='video-wall-tile'
      display='flex'
      h='100%'
      justifyContent='center'
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      overflow='hidden'
      position='relative'
      w='100%'
    >
      <Flex
        align='center'
        bg='bg.overlay'
        justify='space-between'
        left={0}
        opacity={isHovered ? 1 : 0}
        position='absolute'
        px='12px'
        py='8px'
        right={0}
        top={0}
        transition='opacity 0.15s'
        zIndex={1}
      >
        <Text
          color='text.primary'
          fontSize='13px'
          fontWeight='600'
          overflow='hidden'
          textOverflow='ellipsis'
          whiteSpace='nowrap'
        >
          {camera.name}
        </Text>
        <IconButton
          aria-label={t('video_wall:remove_camera')}
          icon={<CloseIcon />}
          onClick={handleRemove}
        />
      </Flex>

      {isLoading && <Spinner size='lg' />}

      {!isLoading && errorMessage && (
        <Flex align='center' direction='column' gap='8px' px='16px'>
          <CameraIcon size={28} />
          <Text color='text.secondary' fontSize='13px' textAlign='center'>
            {t('video_wall:feed_error')}
          </Text>
          <Button onClick={() => refetch()} size='sm' variant='secondary'>
            {t('video_wall:retry')}
          </Button>
        </Flex>
      )}

      {!isLoading && !errorMessage && url && (
        <Box aspectRatio='16 / 9' maxH='100%' maxW='100%' w='100%'>
          <VideoPlayer
            liveOnly
            showControls={false}
            suppressGlobalPlayState
            url={url}
          />
        </Box>
      )}
    </Box>
  )
}
